// src/pages/SharedItineraryPage.js
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../config';
import { useAuth } from '../context/AuthContext';
import './SharedItineraryPage.css';

const TIME_START = 0;
const TIME_END = 48;
const GRID_HEIGHT = 25; // 30 mins = 25px

const SharedAttractionCard = ({ place, top, height, fmtStart }) => {
  return (
    <div
      className="shared-calendar-attraction-card"
      style={{
        top: `${top}px`,
        height: `${height}px`,
      }}
    >
      <div className="shared-attraction-name">{place.name}</div>

      {place.address && (
        <div className="shared-attraction-address">{place.address}</div>
      )}

      <div className="shared-attraction-meta">
        {fmtStart(place.start_time) && <span>Start: {fmtStart(place.start_time)}</span>}
        {place.visit_duration && <span>Duration: {place.visit_duration}h</span>}
      </div>

      {typeof place.travel_time_from_previous === 'number' &&
        place.travel_time_from_previous > 0 && (
          <div className="shared-travel-row">
            <img
              src="/icons/car-side-svgrepo-com.svg"
              alt="Car"
              className="shared-travel-icon"
            />
            <span>Travel from previous: {place.travel_time_from_previous} min</span>
          </div>
        )}
    </div>
  );
};

const SharedDayColumn = ({ dayData, fmtStart }) => {
  return (
    <div className="shared-calendar-column shared-day-column">
      <div className="shared-calendar-day-header">
        <div className="shared-day-title">
          Day {dayData.day}{dayData.city ? ` – ${dayData.city}` : ''}
        </div>
        {dayData.date && <div className="shared-day-date">{dayData.date}</div>}
      </div>

      <div className="shared-grid-background">
        {Array.from({ length: 49 }).map((_, i) => (
          <div key={i} className="shared-grid-hour-line" />
        ))}
      </div>

      {[...(dayData.places || [])]
        .sort((a, b) => a.start_time - b.start_time)
        .map((place, index, arr) => {
          const top = (place.start_time + 0.5) * 4 * GRID_HEIGHT;
          const height = place.visit_duration * 4 * GRID_HEIGHT;

          const prevPlace = index > 0 ? arr[index - 1] : null;
          const prevTop = prevPlace ? (prevPlace.start_time + 0.5) * 4 * GRID_HEIGHT : null;
          const prevHeight = prevPlace ? prevPlace.visit_duration * 4 * GRID_HEIGHT : null;

          const connectorTop = prevPlace ? prevTop + prevHeight : null;
          const connectorBottom = top;
          const connectorHeight = prevPlace ? connectorBottom - connectorTop : null;

          return (
            <React.Fragment key={place.id || `${place.name}-${index}`}>
              {prevPlace && typeof place.travel_time_from_previous === 'number' && (
                <>
                  <div
                    className="shared-dotted-vertical-line"
                    style={{
                      top: `${connectorTop}px`,
                      height: `${connectorHeight}px`,
                    }}
                  />
                  <div
                    className="shared-connector-label"
                    style={{
                      top: `${connectorTop + connectorHeight / 2 - 12}px`,
                    }}
                  >
                    <div className="shared-travel-time-box">
                      <img
                        src="/icons/car-side-svgrepo-com.svg"
                        alt="Car"
                        className="shared-travel-time-icon"
                      />
                      <span className="shared-travel-time-text">
                        {place.travel_time_from_previous} min
                      </span>
                    </div>
                  </div>
                </>
              )}

              <SharedAttractionCard
                place={place}
                top={top}
                height={height}
                fmtStart={fmtStart}
              />
            </React.Fragment>
          );
        })}
    </div>
  );
};
const SharedItineraryPage = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { accessToken, isLoggedIn } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [selectedDayMobile, setSelectedDayMobile] = useState(0);
  const [isSavingShared, setIsSavingShared] = useState(false);
  const desktopCalendarRef = useRef(null);
  const mobileCalendarRef = useRef(null);

  useEffect(() => {
    if (!shareId) return;
    (async () => {
      try {
        const res = await fetch(api.url(`/api/saved-itineraries/shared/${shareId}`));
        if (!res.ok) throw new Error('Invalid or expired link.');
        const json = await res.json();
        setData(json);
        document.title = `${json.title || 'Itinerary'} • Custom Roam`;
      } catch (e) {
        setErr(e.message || 'Failed to load shared itinerary.');
      }
    })();
  }, [shareId]);
  
useEffect(() => {
  const timer = setTimeout(() => {
    const targetHour = 10;
    const rowHeight = 50;   // each 30-minute row in CSS is 50px
    const headerHeight = 60; // sticky header height
    const tenAMIndex = targetHour * 2; // 10:00 = 20 half-hour rows
    const scrollTarget = tenAMIndex * rowHeight - headerHeight;

    if (desktopCalendarRef.current) {
      desktopCalendarRef.current.scrollTop = Math.max(0, scrollTarget);
    }

    if (mobileCalendarRef.current) {
      mobileCalendarRef.current.scrollTop = Math.max(0, scrollTarget);
    }
  }, 0);

  return () => clearTimeout(timer);
}, [data, selectedDayMobile]);
  
   if (err) {
    return (
      <div className="shared-itinerary-page">
        <div className="shared-header-bar">
          <div className="shared-brand" onClick={() => navigate('/')}>
            <img src="/images/customroam-logo.png" alt="Logo" className="shared-logo" />
            <span className="shared-brand-text">CUSTOM ROAM</span>
          </div>
        </div>

        <div className="shared-error-state">
          <h2>Oops</h2>
          <p>{err}</p>
          <button className="shared-primary-btn" onClick={() => navigate('/')}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

    if (!data) {
    return (
      <div className="shared-itinerary-page">
        <div className="shared-header-bar">
          <div className="shared-brand" onClick={() => navigate('/')}>
            <img src="/images/customroam-logo.png" alt="Logo" className="shared-logo" />
            <span className="shared-brand-text">CUSTOM ROAM</span>
          </div>
        </div>

        <div className="shared-loader-state">Loading shared itinerary…</div>
      </div>
    );
  }

  const trip = data.trip_data || {};
  // support both snake_case and camelCase coming from DB
  const start = trip.start_date ?? trip.startDate ?? null;
  const end   = trip.end_date   ?? trip.endDate   ?? null;

   // (optional) prettier date
  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : null);

  const days = Array.isArray(data.itinerary_data) ? data.itinerary_data : [];
  const totalDays = days.length;
    
   // Helper to format 0/0.5 hour indices as HH:MM (e.g., 9 -> 09:00, 9.5 -> 09:30)
   const fmtStart = (v) => {
    if (typeof v !== 'number') return null;
    const h = String(Math.floor(v)).padStart(2, '0');
    const m = v % 1 ? '30' : '00';
    return `${h}:${m}`;
  };
     const handleSaveSharedItinerary = async () => {
    if (!shareId) return;

    const pendingPayload = JSON.stringify({ shareId });

    // Public shared-link users are usually not logged in yet.
    if (!isLoggedIn || !accessToken) {
      sessionStorage.setItem('pendingSharedItinerarySave', pendingPayload);

      navigate('/login', {
        state: {
          from: '/my-itineraries',
          message: 'Log in or create an account to save this itinerary.',
        },
      });
      return;
    }

    try {
      setIsSavingShared(true);

      const response = await fetch(
        api.url(`/api/saved-itineraries/shared/${shareId}/save`),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const msg =
          result?.error ||
          result?.details ||
          result?.message ||
          'Failed to save shared itinerary.';

        const lowerMsg = String(msg).toLowerCase();

        // If token expired / unauthorized, preserve action and send user to login
        if (
          response.status === 401 ||
          response.status === 403 ||
          lowerMsg.includes('access token expired') ||
          lowerMsg.includes('token expired') ||
          lowerMsg.includes('jwt expired')
        ) {
          sessionStorage.setItem('pendingSharedItinerarySave', pendingPayload);

          navigate('/login', {
            state: {
              from: '/my-itineraries',
              message: 'Please log in again to save this itinerary.',
            },
          });
          return;
        }

        throw new Error(msg);
      }

      alert('Itinerary saved to My Itineraries successfully!');
      navigate('/my-itineraries');
    } catch (error) {
      alert(error.message || 'Failed to save shared itinerary.');
    } finally {
      setIsSavingShared(false);
    }
  };
    return (
    <div className="shared-itinerary-page">
            <div className="shared-header-bar">
        <div className="shared-brand" onClick={() => navigate('/')}>
          <img src="/images/customroam-logo.png" alt="Logo" className="shared-logo" />
          <span className="shared-brand-text">CUSTOM ROAM</span>
        </div>

        <div className="shared-header-actions">
          <button
            className="shared-save-btn"
            onClick={handleSaveSharedItinerary}
            disabled={isSavingShared}
          >
            {isSavingShared ? 'Saving...' : 'Save to My Itineraries'}
          </button>

          <button className="shared-header-link" onClick={() => navigate('/')}>
            Create Your Own Itinerary
          </button>
        </div>
      </div>

      <div className="shared-itinerary-container">
                <section className="shared-summary-section">
          <div className="shared-summary-left">
            <p className="shared-eyebrow">Shared Itinerary</p>
            <h1 className="shared-title">{data.title || 'Shared Itinerary'}</h1>

            <div className="shared-summary-meta">
              {trip.destinations && trip.destinations.length > 0 && (
                <div>
                  <strong>Destination:</strong> {trip.destinations.join(', ')}
                </div>
              )}

              {start && end && (
                <div>
                  <strong>Dates:</strong> {fmt(start) || start} – {fmt(end) || end}
                </div>
              )}

              <div>
                <strong>Planned by:</strong> {data.creator_name || 'Custom Roam User'}
              </div>

              <div>
                <strong>Trip Length:</strong> {totalDays} {totalDays === 1 ? 'day' : 'days'}
              </div>
            </div>
          </div>

         
        </section>
                
                <section className="shared-days-section">
          <div className="shared-days-header">
            <h2 className="shared-section-title">Day-by-Day Itinerary</h2>
          </div>

          {/* Desktop calendar view */}
                   <div
            className="shared-calendar-wrapper shared-desktop-only"
            ref={desktopCalendarRef}
          >
            <div className="shared-calendar-grid">
              <div className="shared-calendar-column shared-time-column">
                <div className="shared-cell shared-time-header"></div>
                {Array.from({ length: 48 }).map((_, i) => {
                  const hour = TIME_START + Math.floor(i / 2);
                  const minutes = i % 2 === 0 ? '00' : '30';
                  return (
                    <div key={i} className="shared-cell shared-time-slot">
                      {`${hour.toString().padStart(2, '0')}:${minutes}`}
                    </div>
                  );
                })}
              </div>

              {days.map((day, i) => (
                <SharedDayColumn key={i} dayData={day} fmtStart={fmtStart} />
              ))}
            </div>
          </div>

                  {/* Mobile calendar view */}
          <div className="shared-mobile-calendar shared-mobile-only">
            <div className="shared-day-pill-bar">
              {days.map((day, index) => (
                <button
                  key={index}
                  className={`shared-day-pill ${selectedDayMobile === index ? 'active' : ''}`}
                  onClick={() => setSelectedDayMobile(index)}
                >
                  Day {day.day}
                </button>
              ))}
            </div>

            <div className="shared-mobile-scroll-area" ref={mobileCalendarRef}>
              <div className="shared-calendar-grid shared-mobile-grid">
                <div className="shared-calendar-column shared-time-column">
                  <div className="shared-cell shared-time-header"></div>
                  {Array.from({ length: 48 }).map((_, i) => {
                    const hour = TIME_START + Math.floor(i / 2);
                    const minutes = i % 2 === 0 ? '00' : '30';
                    return (
                      <div
                        key={i}
                        className="shared-cell shared-time-slot"
                      >
                        {`${hour.toString().padStart(2, '0')}:${minutes}`}
                      </div>
                    );
                  })}
                </div>

                {days[selectedDayMobile] && (
                  <SharedDayColumn dayData={days[selectedDayMobile]} fmtStart={fmtStart} />
                )}
              </div>
            </div>
          </div>
        </section>

        
      </div>
    </div>
  );
};

export default SharedItineraryPage;
